import type { BiomarkerResult, FullReport, ReportSection } from '../../types';
import { routedGenerate } from '../ai/providerRouter';
// CLINICAL SAFETY: mockFullReport is loaded via dynamic import ONLY when mockMode === true.
// This ensures Vite tree-shakes all fabricated clinical data out of the production bundle.
// See: mockFullReport.ts for the mock implementation + assertNotProduction guard.
import { getAgentBrand } from '../firestore/agentBrandService';

const REPORT_SYSTEM_PROMPT_TEMPLATE = (role: string) =>
  `You are the ${role} — an expert at interpreting lab results through the lens of functional and longevity medicine. You DO NOT diagnose or prescribe. You compare biomarker values against published functional and optimal ranges, explain what the research says, and help patients have better conversations with their doctors.`;

/**
 * Report Generator (Narrative Report)
 *
 * Takes extracted biomarker data and generates a rich interpretive report —
 * "the lab analysis your doctor would do if they had an hour."
 *
 * Provider: Claude Sonnet (precision-critical)
 */
export async function generateFullReport(
  biomarkers: BiomarkerResult[],
  patientContext: { age: number; sex: 'female' | 'male'; concerns?: string },
  mockMode?: boolean,
): Promise<FullReport> {
  // CLINICAL SAFETY: Mock mode is ONLY for demo/testing (VITE_ENABLE_MOCK=true).
  // When a real API call fails, we throw an error — we NEVER silently substitute
  // a fabricated report. A patient must never receive fake analysis as if it were real.
  if (mockMode) {
    const { getMockFullReport } = await import('./mockFullReport');
    await new Promise((r) => setTimeout(r, 1200));
    return getMockFullReport(biomarkers);
  }

  try {
    const brand = await getAgentBrand();
    const systemPrompt = REPORT_SYSTEM_PROMPT_TEMPLATE(brand.reportEngineRole);
    const userPrompt = buildUserPrompt(biomarkers, patientContext);

    const routerResult = await routedGenerate(
      'report-generator',
      systemPrompt,
      userPrompt,
    );
    if (routerResult.fallbackUsed) {
      console.warn(`[Report Generator] Fallback used: ${routerResult.provider}/${routerResult.model}`);
    }
    const response = routerResult.text;

    return parseReportResponse(response, biomarkers);
  } catch (error) {
    console.error('[Report Generator] Error:', error);
    throw new Error('Unable to generate your report right now. Please try again in a few minutes.');
  }
}

// ---------------------------------------------------------------------------
// Prompt builder
// ---------------------------------------------------------------------------

function buildUserPrompt(
  biomarkers: BiomarkerResult[],
  patientContext: { age: number; sex: 'female' | 'male'; concerns?: string },
): string {
  const biomarkerBlock = biomarkers
    .map(
      (b) =>
        `- ${b.biomarker}: ${b.value} ${b.unit} | Standard range: ${b.standardRange.low}-${b.standardRange.high} | Functional range: ${b.functionalRange.low}-${b.functionalRange.high} | Elite avg: ${b.eliteAverage} | Status: ${b.hemologixStatus} | Interpretation: ${b.interpretation}`,
    )
    .join('\n');

  const concernsLine = patientContext.concerns
    ? `\nPATIENT CONCERNS: ${patientContext.concerns}`
    : '';

  return `Analyze the following lab results and generate a comprehensive narrative report.

PATIENT: ${patientContext.age}-year-old ${patientContext.sex}${concernsLine}

BIOMARKER DATA:
${biomarkerBlock}

Return ONLY valid JSON in this exact format — no markdown, no code fences, no text outside the JSON:
{
  "executiveSummary": "2-3 sentence big picture",
  "keyFindings": [
    {
      "title": "Finding title",
      "content": "Detailed plain-language explanation. No markdown. Written like a knowledgeable friend explaining lab results over coffee. Reference specific numbers. Connect patterns across markers.",
      "biomarkers": ["TSH", "Free T3"],
      "severity": "attention"
    }
  ],
  "categoryAnalyses": [
    {
      "title": "Thyroid Panel",
      "content": "Deep dive into this category...",
      "biomarkers": ["TSH", "Free T3", "Free T4", "TPO Antibodies"],
      "severity": "attention"
    }
  ],
  "patternConnections": [
    {
      "title": "Pattern title",
      "content": "Cross-category insight connecting multiple findings...",
      "biomarkers": ["Fasting Insulin", "hs-CRP", "HbA1c"],
      "severity": "urgent"
    }
  ],
  "positiveFindings": ["Your HDL is excellent at 68 — that's protective."],
  "nextSteps": ["Ask your doctor about..."]
}

RULES:
1. NO markdown in any text — no asterisks, no bullets, no headers, no bold/italic syntax.
2. Write at a 10th-grade reading level.
3. Focus on 3-5 key findings, not 20. Quality over quantity.
4. Every finding MUST reference specific numbers from their labs.
5. Pattern connections should link findings across categories (e.g. thyroid + metabolic + inflammation).
6. Next steps should be concrete and actionable.
7. Include "questions to ask your doctor" within relevant sections.
8. Positive findings should genuinely celebrate what is working well.
9. Voice: warm, direct, knowledgeable friend — not clinical robot.
10. Severity must be one of: "info", "attention", or "urgent".`;
}

// ---------------------------------------------------------------------------
// Response parser
// ---------------------------------------------------------------------------

function parseReportResponse(response: string, biomarkers: BiomarkerResult[]): FullReport {
  try {
    let cleaned = response.trim();
    // Strip markdown code fences if present
    if (cleaned.startsWith('```')) {
      cleaned = cleaned.replace(/^```(?:json)?\n?/, '').replace(/\n?```$/, '');
    }

    const parsed = JSON.parse(cleaned);

    const flaggedCount = biomarkers.filter(
      (b) => b.hemologixStatus !== 'optimal',
    ).length;

    return {
      id: `report-${Date.now()}`,
      generatedAt: new Date().toISOString(),
      executiveSummary: String(parsed.executiveSummary || ''),
      keyFindings: parseReportSections(parsed.keyFindings),
      categoryAnalyses: parseReportSections(parsed.categoryAnalyses),
      patternConnections: parseReportSections(parsed.patternConnections),
      positiveFindings: Array.isArray(parsed.positiveFindings)
        ? parsed.positiveFindings.map(String)
        : [],
      nextSteps: Array.isArray(parsed.nextSteps)
        ? parsed.nextSteps.map(String)
        : [],
      biomarkerCount: biomarkers.length,
      flaggedCount,
    };
  } catch (error) {
    console.error('[Report Generator] Failed to parse response:', error);
    // CLINICAL SAFETY: Never return fabricated/degraded data as a real report.
    // If the AI response can't be parsed, the patient must see an error state.
    throw new Error('Unable to parse your report. Please try again in a few minutes.');
  }
}

function parseReportSections(raw: unknown): ReportSection[] {
  if (!Array.isArray(raw)) return [];

  return raw.map((item: Record<string, unknown>) => ({
    title: String(item.title || ''),
    content: String(item.content || ''),
    biomarkers: Array.isArray(item.biomarkers)
      ? item.biomarkers.map(String)
      : [],
    severity: (['info', 'attention', 'urgent'].includes(String(item.severity))
      ? String(item.severity)
      : 'info') as ReportSection['severity'],
  }));
}

